<template>
    <div class="event-node">
        <div class="event-card" :class="cardClass">
            <span class="event-card-label">{{ anchorLabel }}</span>
            <!-- BEFORE section -->
            <div v-if="event.befores && event.befores.length" class="event-section">
                <div class="event-section-label">Before</div>
                <div
                    v-for="b in event.befores"
                    :key="b.trace.id"
                    class="event-item-group"
                >
                    <div class="event-item" :class="{ 'event-item--toggleable': b.nested && b.nested.length }">
                        <div class="event-item-main">
                            <button
                                v-if="b.nested && b.nested.length"
                                class="event-item-chevron"
                                :aria-expanded="!isCollapsed(b.trace.id)"
                                @click="toggle(b.trace.id)"
                            >
                                <svg width="10" height="10" viewBox="0 0 10 10" :class="{ 'is-open': !isCollapsed(b.trace.id) }">
                                    <path d="M3 2 L7 5 L3 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                            <span v-else class="event-item-chevron event-item-chevron--spacer"></span>
                            <span v-if="b.trace.data.duration != null" class="event-item-duration">
                                <svg class="event-meta-icon" width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M6 1.5h4"></path>
                                    <path d="M8 1.5v2"></path>
                                    <circle cx="8" cy="9.5" r="5"></circle>
                                    <path d="M8 7v2.5l1.5 1"></path>
                                </svg>
                                {{ formatDuration(b.trace.data.duration) }}
                            </span>
                            <a
                                class="event-item-desc"
                                :href="`./#/studio?id=${b.trace.data.moduleId}&t=automation&tid=${b.trace.data.automationId}`"
                                target="_blank"
                            >
                                {{ b.trace.data.description || (b.trace.data.trigger + ' ' + b.trace.data.target) }}
                            </a>
                            <span v-if="b.trace.data.exception" class="event-item-exception">{{ b.trace.data.exception }}</span>
                        </div>
                        <span class="event-item-time">
                            <svg class="event-meta-icon" width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="8" cy="8" r="6.5"></circle>
                                <path d="M8 4.5V8l2.5 1.5"></path>
                            </svg>
                            {{ moment(b.trace.start).format('LTS') }}
                        </span>
                    </div>
                    <div
                        v-if="b.nested && b.nested.length && !isCollapsed(b.trace.id)"
                        class="event-item-children"
                    >
                        <AutomationRunTrace
                            v-for="(sub, idx) in b.nested"
                            :key="sub.id || ('bsub-' + idx)"
                            :historyId="historyId"
                            :event="sub"
                        ></AutomationRunTrace>
                    </div>
                </div>
            </div>

            <!-- EVENT anchor -->
            <div class="event-anchor" :class="anchorClass">
                <div class="event-anchor-body">
                    <a
                        class="event-anchor-module"
                        :href="'./#/studio?id=' + event.activity.data.moduleId"
                        target="_blank"
                    >
                        <fts-moji
                            v-if="moduleEmoji(event.activity.data.moduleId)"
                            class="event-module-emoji"
                            :emoji="moduleEmoji(event.activity.data.moduleId)"
                            :size="16"
                        />
                        {{ event.activity.data.module }}
                    </a>
                    <span class="event-sep">›</span>
                    <span v-if="event.activity.type == 1" class="event-anchor-action">
                        {{ getActivityAction(event.activity) }}
                    </span>
                    <span v-else class="event-anchor-action">
                        <a
                            :href="`./#/studio?id=${event.activity.data.moduleId}&t=automation&tid=${event.activity.data.automationId}`"
                            target="_blank"
                        >
                            {{ event.activity.data.trigger }} {{ event.activity.data.target }}
                        </a>
                    </span>
                    <template v-if="event.activity.data.entry">
                        <span class="event-for">for</span>
                        <a
                            class="event-entry"
                            :href="'./#/dashboard?id=' + event.activity.data.entryId"
                            target="_blank"
                        >
                            {{ event.activity.data.entry }}
                        </a>
                    </template>
                </div>
                <span class="event-anchor-time">
                    <svg class="event-meta-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="8" cy="8" r="6.5"></circle>
                        <path d="M8 4.5V8l2.5 1.5"></path>
                    </svg>
                    {{ moment(event.activity.start).format('LTS') }}
                </span>
            </div>

            <!-- AFTER section -->
            <div v-if="event.afters && event.afters.length" class="event-section">
                <div class="event-section-label">After</div>
                <div
                    v-for="a in event.afters"
                    :key="a.trace.id"
                    class="event-item-group"
                >
                    <div class="event-item" :class="{ 'event-item--toggleable': a.nested && a.nested.length }">
                        <div class="event-item-main">
                            <button
                                v-if="a.nested && a.nested.length"
                                class="event-item-chevron"
                                :aria-expanded="!isCollapsed(a.trace.id)"
                                @click="toggle(a.trace.id)"
                            >
                                <svg width="10" height="10" viewBox="0 0 10 10" :class="{ 'is-open': !isCollapsed(a.trace.id) }">
                                    <path d="M3 2 L7 5 L3 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                            <span v-else class="event-item-chevron event-item-chevron--spacer"></span>
                            <span v-if="a.trace.data.duration != null" class="event-item-duration">
                                <svg class="event-meta-icon" width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M6 1.5h4"></path>
                                    <path d="M8 1.5v2"></path>
                                    <circle cx="8" cy="9.5" r="5"></circle>
                                    <path d="M8 7v2.5l1.5 1"></path>
                                </svg>
                                {{ formatDuration(a.trace.data.duration) }}
                            </span>
                            <a
                                class="event-item-desc"
                                :href="`./#/studio?id=${a.trace.data.moduleId}&t=automation&tid=${a.trace.data.automationId}`"
                                target="_blank"
                            >
                                {{ a.trace.data.description || (a.trace.data.trigger + ' ' + a.trace.data.target) }}
                            </a>
                            <span v-if="a.trace.data.exception" class="event-item-exception">{{ a.trace.data.exception }}</span>
                        </div>
                        <span class="event-item-time">
                            <svg class="event-meta-icon" width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="8" cy="8" r="6.5"></circle>
                                <path d="M8 4.5V8l2.5 1.5"></path>
                            </svg>
                            {{ moment(a.trace.start).format('LTS') }}
                        </span>
                    </div>
                    <div
                        v-if="a.nested && a.nested.length && !isCollapsed(a.trace.id)"
                        class="event-item-children"
                    >
                        <AutomationRunTrace
                            v-for="(sub, idx) in a.nested"
                            :key="sub.id || ('asub-' + idx)"
                            :historyId="historyId"
                            :event="sub"
                        ></AutomationRunTrace>
                    </div>
                </div>
            </div>
        </div>

        <!-- Nested sub-events at event-level (rare) -->
        <div v-if="event.nested && event.nested.length" class="event-nested">
            <AutomationRunTrace
                v-for="(sub, idx) in event.nested"
                :key="sub.id || ('sub-' + idx)"
                :historyId="historyId"
                :event="sub"
            ></AutomationRunTrace>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { clientStore } from "../store/clientStore";
import FTSMoji from "../elements/FTSMoji";

export default {
    name: 'AutomationRunTrace',
    components: {
        "fts-moji": FTSMoji
    },
    props: {
        event: Object,
        historyId: String
    },
    inject: {
        traceControl: { default: null }
    },
    data() {
        return {
            collapsed: {}
        };
    },
    watch: {
        'traceControl.expandAllAt'() {
            this.collapsed = {};
        },
        'traceControl.collapseAllAt'() {
            const next = {};
            (this.event.befores || []).forEach(b => {
                if (b.nested && b.nested.length) next[b.trace.id] = true;
            });
            (this.event.afters || []).forEach(a => {
                if (a.nested && a.nested.length) next[a.trace.id] = true;
            });
            this.collapsed = next;
        }
    },
    computed: {
        isCurrent() {
            const a = this.event && this.event.activity;
            return a && a.data.id != null && this.historyId == a.data.id.toString();
        },
        anchorClass() {
            if (this.isCurrent) return 'event-anchor--current';
            if (this.event.activity && this.event.activity.type === 1) return 'event-anchor--activity';
            return 'event-anchor--automation';
        },
        cardClass() {
            if (this.isCurrent) return 'event-card--current';
            if (this.event.activity && this.event.activity.type === 1) return 'event-card--activity';
            return 'event-card--automation';
        },
        anchorLabel() {
            if (this.isCurrent) return 'Current';
            if (this.event.activity && this.event.activity.type === 1) return 'Activity';
            return 'Automation';
        }
    },
    methods: {
        moment,
        formatDuration(ms) {
            if (ms == null) return '';
            return (ms / 1000).toFixed(2) + 's';
        },
        getActivityAction(trace) {
            if (trace.data.activity) return trace.data.activity;
            return trace.data.tempActivityHistoryId;
        },
        moduleEmoji(moduleId) {
            const mods = (clientStore.state && clientStore.state.workspaceModules) || [];
            const mod = mods.find(m => m.id == moduleId);
            return mod && mod.emoji ? mod.emoji : '';
        },
        isCollapsed(id) {
            return !!this.collapsed[id];
        },
        toggle(id) {
            this.$set(this.collapsed, id, !this.collapsed[id]);
        }
    }
};
</script>

<style scoped>
/* Subgrid inheritance: every wrapper spans both columns and passes the
   column tracks down, so all .event-item-time / .event-anchor-time land
   in the same rightmost column regardless of nesting depth. */
.event-node,
.event-card,
.event-section,
.event-item-group,
.event-item-children,
.event-item,
.event-anchor,
.event-nested {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
}

.event-node {
    row-gap: var(--inistate-spacing-4);
}

.event-card {
    position: relative;
    row-gap: var(--inistate-spacing-4);
    padding: var(--inistate-spacing-20) var(--inistate-spacing-8) var(--inistate-spacing-8);
    background: var(--inistate-color-shade-white);
    border: 1px solid var(--inistate-color-neutral-200);
    border-left-width: var(--inistate-spacing-4);
    border-radius: var(--inistate-rounded-4);
    transition: box-shadow 0.15s ease, border-left-color 0.15s ease;
}

.event-card:hover {
    box-shadow: 0 1px var(--inistate-spacing-4) rgba(0, 0, 0, 0.05);
}

.event-card--activity:hover,
.event-item:hover + .event-item-children .event-card--activity {
    border-left-color: var(--inistate-color-primary-500);
}

.event-card--automation:hover,
.event-item:hover + .event-item-children .event-card--automation {
    border-left-color: var(--inistate-color-green-600);
}

.event-card--current:hover,
.event-item:hover + .event-item-children .event-card--current {
    border-left-color: var(--inistate-color-orange-500);
}

.event-item:hover + .event-item-children .event-card {
    box-shadow: 0 1px var(--inistate-spacing-4) rgba(0, 0, 0, 0.05);
}

.event-card-label {
    position: absolute;
    top: 0;
    left: 0;
    padding: var(--inistate-spacing-2) var(--inistate-spacing-8);
    font-size: var(--inistate-label-text-10);
    line-height: var(--inistate-label-text-10--line-height);
    font-weight: var(--inistate-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-radius: 0 0 var(--inistate-rounded-2) 0;
    background: var(--inistate-color-neutral-100);
    color: var(--inistate-color-neutral-500);
    white-space: nowrap;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.event-card--activity:hover > .event-card-label,
.event-item:hover + .event-item-children .event-card--activity > .event-card-label {
    background: var(--inistate-color-primary-500);
    color: var(--inistate-color-shade-white);
}

.event-card--automation:hover > .event-card-label,
.event-item:hover + .event-item-children .event-card--automation > .event-card-label {
    background: var(--inistate-color-green-600);
    color: var(--inistate-color-shade-white);
}

.event-card--current:hover > .event-card-label,
.event-item:hover + .event-item-children .event-card--current > .event-card-label {
    background: var(--inistate-color-orange-500);
    color: var(--inistate-color-shade-white);
}

/* Section (Before / After) */
.event-section {
    row-gap: var(--inistate-spacing-2);
}

.event-section-label {
    grid-column: 1 / -1;
    font-size: var(--inistate-label-text-10);
    line-height: var(--inistate-label-text-10--line-height);
    font-weight: var(--inistate-font-weight-bold);
    color: var(--inistate-color-neutral-400);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0 var(--inistate-spacing-8);
}

.event-item-group {
    row-gap: var(--inistate-spacing-2);
}

/* Item row: main content (col 1) + time (col 2) */
.event-item {
    align-items: center;
    column-gap: var(--inistate-spacing-8);
    padding: var(--inistate-spacing-4) var(--inistate-spacing-8);
    border-radius: var(--inistate-rounded-4);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: var(--inistate-text-primary);
    transition: background-color 0.1s ease;
}

.event-item:hover {
    background: var(--inistate-color-neutral-50);
}

.event-item-main {
    grid-column: 1;
    display: flex;
    align-items: center;
    gap: var(--inistate-spacing-4);
    min-width: 0;
}

.event-item-chevron {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--inistate-spacing-16);
    height: var(--inistate-spacing-16);
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--inistate-color-neutral-400);
    cursor: pointer;
    border-radius: var(--inistate-rounded-2);
}

.event-item-chevron:hover {
    background: var(--inistate-color-neutral-100);
    color: var(--inistate-color-neutral-700);
}

.event-item-chevron--spacer {
    cursor: default;
    pointer-events: none;
}

.event-item-chevron svg {
    transition: transform 0.15s ease;
}

.event-item-chevron svg.is-open {
    transform: rotate(90deg);
}

.event-item-desc {
    flex: 1;
    min-width: 0;
    color: var(--inistate-text-primary);
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.event-item-desc:hover {
    color: var(--inistate-color-primary-600);
    text-decoration: underline;
}

.event-item-exception {
    color: var(--inistate-text-error);
    font-size: var(--inistate-label-text-12);
    font-weight: var(--inistate-font-weight-medium);
}

.event-item-duration {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: var(--inistate-spacing-2);
    min-width: var(--inistate-spacing-56);
    font-size: var(--inistate-label-text-12);
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-secondary);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.event-item-time {
    grid-column: 2;
    display: inline-flex;
    align-items: center;
    gap: var(--inistate-spacing-2);
    font-size: var(--inistate-label-text-12);
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-secondary);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

/* Event anchor: body (col 1) + time (col 2) */
.event-anchor {
    align-items: center;
    column-gap: var(--inistate-spacing-8);
    padding: var(--inistate-spacing-8);
    border-radius: var(--inistate-rounded-4);
    transition: background-color 0.1s ease;
}

.event-anchor:hover {
    background: var(--inistate-color-neutral-50);
}

.event-anchor-body {
    grid-column: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--inistate-spacing-4);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    min-width: 0;
}

.event-anchor-module {
    display: inline-flex;
    align-items: center;
    gap: var(--inistate-spacing-4);
    font-weight: var(--inistate-font-weight-bold);
    color: var(--inistate-color-neutral-900);
    text-decoration: none;
}

.event-anchor-module:hover {
    text-decoration: underline;
}

.event-module-emoji {
    display: inline-block;
    flex-shrink: 0;
}

.event-sep {
    color: var(--inistate-color-neutral-300);
    font-weight: var(--inistate-font-weight-bold);
}

.event-anchor-action {
    color: var(--inistate-text-primary);
    font-weight: var(--inistate-font-weight-medium);
}

.event-anchor-action a {
    color: var(--inistate-color-primary-600);
    text-decoration: none;
}

.event-anchor-action a:hover {
    text-decoration: underline;
}

.event-for {
    color: var(--inistate-color-neutral-400);
    font-size: var(--inistate-label-text-12);
}

.event-entry {
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-color-neutral-900);
    text-decoration: none;
    background: var(--inistate-color-neutral-100);
    padding: 0 var(--inistate-spacing-4);
    border-radius: var(--inistate-rounded-2);
}

.event-entry:hover {
    background: var(--inistate-color-neutral-200);
}

.event-anchor-time {
    grid-column: 2;
    display: inline-flex;
    align-items: center;
    gap: var(--inistate-spacing-4);
    font-size: var(--inistate-label-text-12);
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-secondary);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.event-meta-icon {
    flex-shrink: 0;
    color: var(--inistate-color-neutral-400);
}

/* Nested children of an item: indent col 1 via padding-left of main content,
   not the whole container — subgrid col 2 still aligns globally. */
.event-item-children {
    row-gap: var(--inistate-spacing-4);
    padding-top: var(--inistate-spacing-4);
    padding-bottom: var(--inistate-spacing-4);
}

.event-item-children > .event-node {
    position: relative;
}

.event-item-children > .event-node::before {
    content: "";
    position: absolute;
    left: var(--inistate-spacing-8);
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--inistate-color-neutral-200);
    pointer-events: none;
}

/* Nested sub-events at event-level */
.event-nested {
    row-gap: var(--inistate-spacing-4);
    margin-top: var(--inistate-spacing-4);
}
</style>
